import { buildReportScopePromptSection } from '@process-advisor/process-miner/consultant/aiReportScope';
// ============================================================================
// rovoPack-prompt.js
//
// Builds the AI prompt for the Rovo Agent Pack.
// Produces deployment-ready Rovo Agent specifications — concrete enough
// for a Jira admin to implement — based on the workflow's actual evidence.
// ============================================================================

export const buildRovoPackPrompt = ({ transitionsJSON, variantsJSON, issueTypeStr, metricsHistory = [], sampleItems = [], fieldUsage = {}, statusTypeSummary = null, activityType = null, reportScopeSummary = {} }) => {
  const reportScopeText = buildReportScopePromptSection(reportScopeSummary);
  const inputText = JSON.stringify({
    issue_type:          issueTypeStr,
    metrics_history:     metricsHistory,
    transitions:         transitionsJSON,
    variants:            variantsJSON,
    sample_items:        (sampleItems || []).slice(0, 10),
    field_usage:         fieldUsage,
    activity_context:    { activity_field: activityType, activity_label: activityType },
    activity_types:       statusTypeSummary,
  }, null, 2);

  return `You are an expert Atlassian Rovo consultant. Based on the process mining evidence below, produce a Rovo Agent Pack — a set of deployment-ready Rovo Agent specifications tailored to this specific workflow.

PLATFORM CONTEXT
The target platform is Jira Cloud with Atlassian Rovo enabled. The four Rovo products are:
  - Rovo Agents: autonomous agents that act inside Jira (route, close, label, escalate, comment). Built in Rovo Studio or via standard agent templates. Triggered by Jira Automation rules or webhooks.
  - Rovo Search: AI-powered search across Jira, Confluence, and connected tools. Surfaces similar past issues, KB articles, and resolutions at point-of-work. No custom build required.
  - Rovo Chat: conversational AI embedded in Jira tickets. Helps agents summarise threads, draft resolutions, find related issues, suggest next steps. No custom build required.
  - Rovo Studio: low-code builder for purpose-built Rovo Agents with custom logic, multi-step actions, or integrations. Requires Rovo premium.

RULES
- Base every agent on a specific named signal in the evidence (a transition, a rework rate, a variant, a field pattern).
- Never say "an AI model" — always name the specific Rovo product.
- Each agent must have a specific workflow trigger — not a generic one.
- pack_title must always be exactly 'Rovo Agent Pack' — no workflow-specific subtitle.
- Do not include workflow_context or discovery_rules_summary — process analysis is in the Process Health tab. Start with rovo_readiness then go straight to agents.
- agent_instructions must be the exact text a Jira admin pastes into the Rovo Agent Instructions field. Write it as direct instructions to the agent — imperative, step-by-step, referencing actual field names and statuses from the evidence.
- jira_automation_json must be a valid Jira Automation Cloud export object that can be imported directly via Project Settings → Automation → Import. Use real trigger types (jira.issue.created, jira.issue.transitioned, jira.jql.scheduled), real condition types (jira.issue.condition.jql), and real action types. For scheduled rules set trigger.type to jira.jql.scheduled and include value.jql with a real JQL string. For webhook actions use jira.issue.outgoing.webhook with value.url as a placeholder.
- The jira_automation_pairing field is a human-readable description (separate from jira_automation_json).
- Provide 5 to 7 agents ordered by implementation priority (quick wins first).
- Use plain text only. Return JSON only. No markdown fences.

OUTPUT FORMAT
Return exactly this JSON:

{
  "pack_id": "<short slug e.g. rovo-incident-pack-v1>",
  "pack_title": "Rovo Agent Pack",
  "rovo_readiness": "High | Medium | Low",
  "rovo_readiness_rationale": "<1 sentence>",
  "agents": [
    {
      "id": "<slug>",
      "name": "<specific agent name — not generic>",
      "rovo_product": "Rovo Agents | Rovo Search | Rovo Chat | Rovo Studio",
      "priority": "Critical | High | Medium",
      "requires_studio": false,
      "evidence_signal": "<the specific transition, rework rate, or variant driving this>",
      "purpose": "<1 sentence — what this agent does and why>",
      "trigger": "<exact workflow event: e.g. Issue created, Status transitions to In Progress, Issue age exceeds 48 hours>",
      "actions": ["<concrete action 1>", "<concrete action 2>"],
      "jira_automation_pairing": "<description of the Jira Automation rule that triggers or works with this agent>",
      "rovo_studio_config": "<if requires_studio=true: what the Studio agent needs to do; else: null>",
      "expected_benefit": "<measurable outcome in business terms>",
      "setup_effort": "Low | Medium | High",
      "prerequisites": ["<prerequisite 1>", "<prerequisite 2>"],
      "agent_instructions": "<the exact text to paste into the Rovo Agent or Rovo Studio Instructions field — written as a direct instruction to the agent, e.g. 'When an issue is created: 1. Analyse the summary and description. 2. Search for similar resolved issues. 3. Set the Assignee field to...' Be specific and actionable.>",
      "jira_automation_json": {
        "name": "<rule name>",
        "state": "ENABLED",
        "canOtherRuleTriggerThis": false,
        "shareTeam": false,
        "tags": [],
        "trigger": {
          "component": "TRIGGER",
          "type": "<jira.issue.transitioned | jira.issue.created | jira.jql.scheduled | jira.field.value.changed>",
          "value": {}
        },
        "components": [
          {
            "component": "CONDITION",
            "type": "jira.issue.condition.jql",
            "value": { "queryString": "<valid JQL for this rule's scope>" }
          },
          {
            "component": "ACTION",
            "type": "<jira.issue.outgoing.webhook | jira.issue.field.edit | jira.issue.transition | jira.comment.add>",
            "value": { "<field>": "<value>" }
          }
        ]
      }
    }
  ],
  "implementation_sequence": {
    "phase_1_no_build": {
      "label": "Immediate — no build required",
      "description": "<what Rovo Chat and Rovo Search deliver out of the box for this workflow>",
      "agents": ["<agent id>"]
    },
    "phase_2_agents": {
      "label": "Short term — standard Rovo Agents",
      "description": "<agents deployable via standard Rovo Agents without Studio>",
      "agents": ["<agent id>"]
    },
    "phase_3_studio": {
      "label": "Medium term — Rovo Studio",
      "description": "<agents requiring custom Studio builds>",
      "agents": ["<agent id>"]
    }
  },
}

${reportScopeText}
PROCESS EVIDENCE
${inputText}`;
};
